<script setup>
import {computed} from "vue";
import {getXaxisDates} from "@/charts";
import SourceGA from "@/components/reporting/partials/SourceGA.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import ReportingEmpty from "@/components/reporting/partials/ReportingEmpty.vue";
import useTrafficStore from "@/composables/useTrafficStore";
import useAccountMissing from "@/composables/useAccountMissing";
import Reporting from "@/services/reporting";
import {useQueries} from "@tanstack/vue-query";
import useQueriesState from "@/composables/useQueriesState";
import ErrorBlock from "@/components/reporting/partials/ErrorBlock.vue";
import ConnectAccount from "@/components/reporting/partials/ConnectAccount.vue";
import LineChart from "@/components/global-themed/charts/LineChart.vue";

const {fromDate, toDate, compareFromDate, compareToDate} = useTrafficStore();

const {isAccountMissing, setIsAccountMissing, checkResponseForAccountMissing} = useAccountMissing();

const fetchVisitors = async (fromDate, toDate) => {
  if (!fromDate || !toDate) return null;
  setIsAccountMissing(false);
  try {
    return (await Reporting.fetchBase(fromDate, toDate)).data.data;
  } catch (e) {
    checkResponseForAccountMissing(e.response);
    throw e;
  }
}

const results = useQueries({
  queries: [
    {
      queryKey: ['reporting-visitors', 'reporting', fromDate, toDate],
      queryFn: async () => fetchVisitors(fromDate.value, toDate.value),
    },
    {
      queryKey: ['reporting-visitors', 'reporting', compareFromDate, compareToDate],
      queryFn: async () => fetchVisitors(compareFromDate.value, compareToDate.value),
    },
  ],
});

const {data, comparedData, isLoading: loading, isError} = useQueriesState(results);

const xAxis = computed(() => getXaxisDates(data.value, comparedData.value));

const series = computed(() => {
  const series = [
    {
      name: 'Visitors',
      data: xAxis.value?.map((date, index) => ({
        x: new Date(date).getTime(),
        y: data.value[index]?.visitors || null,
      })) ?? [],
    }
  ];

  if (!comparedData.value) return series;

  return [
    ...series,
    {
      name: 'Previous Visitors',
      data:
        xAxis.value?.map((date, index) => ({
          x: new Date(date).getTime(),
          y: comparedData.value[index]?.visitors || null,
        })) ?? [],
    },
  ];
});

const options = {
  xaxis: {
    tooltip: {
      enabled: false
    },
    type: 'datetime',
    labels: {
      style: {
        cssClass: 'apexcharts-xaxis-label',
      }
    }
  },
  yaxis: {
    min: 0,
    labels: {
      style: {
        cssClass: 'apexcharts-xaxis-label',
      }
    }
  },
};

</script>

<template>
  <div class="tw-w-full">
    <h3 class="tw-text-xl tw-text-font-primary tw-font-bold tw-font-sans tw-mb-6">
      Website Visitors
    </h3>

    <div
      v-if="!data?.length"
      class="tw-p-6 tw-rounded-lg tw-border-2 tw-w-full tw-mb-8"
    >
      <LoadingBlock v-if="loading" />
      <ConnectAccount v-else-if="isAccountMissing" />
      <ErrorBlock v-else-if="isError" />
      <ReportingEmpty v-else />

      <SourceGA />
    </div>
    <div
      v-else
      class="tw-p-6 tw-rounded-lg tw-border-2 tw-mt-16 tw-mb-8"
    >
      <LineChart
        :series="series"
        :options="options"
      />

      <SourceGA />
    </div>
  </div>
</template>
